import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, Link, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { anfrageErledigen, kundeLoeschen, kundenAkte, lizenzEntsperren, lizenzLoeschen, modulListe } from '../api';
import { Ampel, Auslastung, Bestaetigung, Kopieren, Mono, Paar, paarRaster, StatusPille } from '../komponenten/Bausteine';
import KundeDialog from '../komponenten/KundeDialog';
import LizenzDialog from '../komponenten/LizenzDialoge';
import { datum, fehlertext, MODUS, seit, zeitpunkt } from '../utils/format';

const kopfzeile = { fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', lineHeight: 1.6 };

function LizenzKarte({ l, module, oeffne, onEntsperren, onLoeschen }) {
  const aktiviert = !!l.lizenz_key;
  const sperreKommt = l.gesperrt && l.status !== 'gesperrt';
  const nichtGebucht = module.alle.filter((m) => !l.module.includes(m)).length;
  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="overline" sx={kopfzeile}>Lizenz</Typography>
          <StatusPille status={l.status} tage={l.tage_bis_ablauf} />
        </Box>

        {l.status === 'gesperrt' && <Alert severity="error">Gesperrt: {l.gesperrt_grund}</Alert>}
        {sperreKommt && <Alert severity="warning">Wird am {datum(l.gesperrt_ab)} gesperrt: {l.gesperrt_grund}</Alert>}
        {l.status === 'ohne_bindung' && <Alert severity="info">Schlüssel ohne Gerätebindung – läuft auf jedem Rechner.</Alert>}

        <Box sx={paarRaster}>
          <Paar begriff="Gültig bis">{l.gueltig_bis ? datum(l.gueltig_bis) : 'unbefristet'}</Paar>
          <Paar begriff="Module">
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {l.module.map((m) => <Chip key={m} size="small" label={module.bezeichnungen[m] || m} sx={{ bgcolor: 'rgba(245,196,0,0.18)' }} />)}
              {l.module.length === 0 && <Typography variant="body2" color="text.secondary">keine Zusatzmodule</Typography>}
              {nichtGebucht > 0 && <Chip size="small" variant="outlined" label={`+ ${nichtGebucht} nicht gebucht`} />}
            </Box>
          </Paar>
          <Paar begriff="Arbeitsplätze"><Auslastung belegt={l.belegte_arbeitsplaetze} max={l.max_arbeitsplaetze} /></Paar>
          <Paar begriff="Mandanten"><Auslastung belegt={l.angelegte_mandanten} max={l.max_mandanten} wort="angelegt" /></Paar>
          <Paar begriff="Gerät">
            {l.hardware_id
              ? <><Mono>{l.hardware_id}</Mono><Kopieren text={l.hardware_id} titel="Hardware-ID kopieren" /></>
              : <Typography variant="body2" color="text.secondary">{aktiviert ? 'ohne Bindung' : 'noch nicht aktiviert'}</Typography>}
          </Paar>
          <Paar begriff="Aktivierungscode"><Mono>{l.code}</Mono><Kopieren text={l.code} titel="Code kopieren" /></Paar>
          {aktiviert && (
            <Paar begriff="Schlüssel">
              <Mono sx={{ display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{l.lizenz_key}</Mono>
              <Kopieren text={l.lizenz_key} titel="Schlüssel kopieren" />
            </Paar>
          )}
          <Paar begriff="Aktiviert">{l.aktiviert_am ? zeitpunkt(l.aktiviert_am) : '–'}</Paar>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" onClick={() => oeffne('verlaengern', l)}>Verlängern</Button>
          <Button size="small" variant="outlined" onClick={() => oeffne('module', l)}>Module ändern</Button>
          <Button size="small" variant="outlined" onClick={() => oeffne('kontingent', l)}>Kontingent ändern</Button>
          {aktiviert
            ? <Button size="small" variant="outlined" onClick={() => oeffne('geraet', l)}>Gerät wechseln</Button>
            : (
              <>
                <Button size="small" variant="outlined" onClick={() => oeffne('key', l)}>Schlüssel für Gerät erzeugen</Button>
                <Button size="small" variant="outlined" color="warning" onClick={() => oeffne('key_frei', l)}>Schlüssel ohne Bindung</Button>
              </>
            )}
          <Box sx={{ flexGrow: 1 }} />
          {l.gesperrt
            ? <Button size="small" variant="outlined" onClick={() => onEntsperren(l)}>Sperre aufheben</Button>
            : aktiviert && <Button size="small" variant="outlined" color="error" onClick={() => oeffne('sperren', l)}>Sperren</Button>}
          {(!aktiviert || l.status === 'gesperrt') && <Button size="small" color="error" onClick={() => onLoeschen(l)}>Löschen</Button>}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Kundenakte() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [akte, setAkte] = useState(null);
  const [module, setModule] = useState({ alle: [], bezeichnungen: {} });
  const [fehler, setFehler] = useState('');
  const [erfolg, setErfolg] = useState('');
  const [dialog, setDialog] = useState(null);        // { art, lizenz, vorgabe }
  const [bearbeiten, setBearbeiten] = useState(false);
  const [frage, setFrage] = useState(null);

  const laden = useCallback(() => kundenAkte(id)
    .then((r) => { setAkte(r.data); return r.data; })
    .catch((e) => { setFehler(fehlertext(e, 'Akte konnte nicht geladen werden.')); return null; }), [id]);

  useEffect(() => {
    modulListe().then((r) => setModule({ alle: r.data.alle_module, bezeichnungen: r.data.bezeichnungen })).catch(() => {});
  }, []);

  // Sprung aus der Uebersicht: ?aktion=verlaengern&lizenz=3 oeffnet gleich den Dialog
  useEffect(() => {
    laden().then((daten) => {
      const aktion = params.get('aktion');
      if (!daten || !aktion) return;
      const wunsch = Number(params.get('lizenz'));
      const lizenz = daten.lizenzen.find((l) => l.id === wunsch)
        || (aktion === 'geraet' ? daten.lizenzen.find((l) => l.hardware_id) : null)
        || daten.lizenzen[0] || null;
      if (aktion === 'neu' || lizenz) {
        setDialog({ art: aktion, lizenz: aktion === 'neu' ? null : lizenz, vorgabe: { hardware_id: params.get('hw') || '' } });
      }
      setParams({}, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!akte) return fehler ? <Alert severity="error">{fehler}</Alert> : null;
  const { kunde, lizenzen, installationen, verlauf, anfragen } = akte;
  const offeneAnfragen = anfragen.filter((a) => !a.erledigt_am);
  const kontakt = [kunde.ansprechpartner, kunde.kontakt_email, kunde.telefon].filter(Boolean);

  const fertig = (meldung) => { setDialog(null); setErfolg(meldung); setFehler(''); laden(); };
  const entsperren = async (l) => {
    try { await lizenzEntsperren(l.id); fertig('Sperre aufgehoben.'); } catch (e) { setFehler(fehlertext(e)); }
  };

  return (
    <Box>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/kunden')} sx={{ mb: 1, color: 'text.secondary' }}>Alle Kunden</Button>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {kunde.anzeige}{kunde.ist_testkunde && <Chip size="small" label="Testkunde" sx={{ ml: 1.5, verticalAlign: 'middle' }} />}
          </Typography>
          {kunde.anzeige !== kunde.name && (
            <Typography variant="body2" color="text.secondary">Lizenzname: {kunde.name}<Kopieren text={kunde.name} titel="Lizenzname kopieren" /></Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {kontakt.length ? kontakt.join(' · ') : 'Noch keine Kontaktdaten hinterlegt'}
            {kunde.erstellt_am && ` · Kunde seit ${datum(kunde.erstellt_am)}`}
          </Typography>
          {kunde.notiz && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', maxWidth: '70ch' }}>{kunde.notiz}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => setBearbeiten(true)}>Kunde bearbeiten</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ art: 'neu', lizenz: null })}>Neue Lizenz</Button>
        </Box>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFehler('')}>{fehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setErfolg('')}>{erfolg}</Alert>}
      {offeneAnfragen.map((a) => (
        <Alert
          key={a.id} severity="warning" sx={{ mb: 2 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small" variant="contained"
                onClick={() => setDialog(lizenzen.length ? { art: 'verlaengern', lizenz: lizenzen[lizenzen.length - 1] } : { art: 'neu', lizenz: null })}
              >
                {lizenzen.length ? 'Verlängern' : 'Lizenz anlegen'}
              </Button>
              <Button
                size="small" color="inherit"
                onClick={async () => {
                  try { await anfrageErledigen(a.id); fertig('Anfrage als erledigt markiert.'); } catch (e) { setFehler(fehlertext(e)); }
                }}
              >
                Erledigt
              </Button>
            </Box>
          )}
        >
          Verlängerungsanfrage vom {zeitpunkt(a.erstellt_am)}: {[a.name, a.email, a.telefon].filter(Boolean).join(' · ')}{a.nachricht ? ` – „${a.nachricht}“` : ''}
        </Alert>
      ))}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.3fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          {lizenzen.map((l) => (
            <LizenzKarte
              key={l.id} l={l} module={module}
              oeffne={(art, lizenz) => setDialog({ art, lizenz })}
              onEntsperren={entsperren}
              onLoeschen={(lizenz) => setFrage({ art: 'lizenz', lizenz })}
            />
          ))}
          {lizenzen.length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography sx={{ fontWeight: 600 }}>Noch keine Lizenz</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Eine Lizenz legt Module, Arbeitsplätze und Laufzeit fest und erzeugt den Aktivierungscode für den Kunden.
                </Typography>
                <Button variant="contained" onClick={() => setDialog({ art: 'neu', lizenz: null })}>Lizenz anlegen</Button>
                <Box><Button size="small" color="error" sx={{ mt: 2 }} onClick={() => setFrage({ art: 'kunde' })}>Kunde löschen</Button></Box>
              </CardContent>
            </Card>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="overline" sx={kopfzeile}>{installationen.length > 1 ? 'Installationen' : 'Installation'}</Typography>
              {installationen.length === 0 && (
                <Typography variant="body2" color="text.secondary">Noch keine Meldung. ProfiWare meldet sich nach der Aktivierung stündlich.</Typography>
              )}
              {installationen.map((i, n) => (
                <Box key={i.id} sx={{ ...paarRaster, pt: n ? 2 : 0, borderTop: n ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Paar begriff="Zuletzt gemeldet"><Ampel farbe={i.ampel}>{seit(i.letzter_kontakt)}</Ampel></Paar>
                  <Paar begriff="Version">
                    {i.aktuelle_version || '–'}{' '}
                    {i.veraltet && <Chip size="small" color="warning" label={`aktuell ist ${akte.neueste_version}`} />}
                  </Paar>
                  <Paar begriff="Betriebsart">{MODUS[i.modus] || i.modus || '–'}</Paar>
                  {i.letzter_status === 'hardware_mismatch' && (
                    <Paar begriff="Hinweis">
                      <Typography variant="body2" color="error">Meldet sich von einem anderen Gerät</Typography>
                      <Mono>{i.hardware_id}</Mono><Kopieren text={i.hardware_id} titel="Hardware-ID kopieren" />
                    </Paar>
                  )}
                  <Paar begriff="Erste Meldung">{datum(i.erster_kontakt)}</Paar>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="overline" sx={kopfzeile}>Verlauf</Typography>
              {verlauf.length === 0 && <Typography variant="body2" color="text.secondary">Noch keine Einträge.</Typography>}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
                {verlauf.map((p) => (
                  <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: '5.6rem minmax(0, 1fr)', gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" title={zeitpunkt(p.zeitpunkt)} sx={{ fontVariantNumeric: 'tabular-nums' }}>{datum(p.zeitpunkt)}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      {p.text}{p.benutzer && <Box component="span" sx={{ color: 'text.secondary' }}> · {p.benutzer}</Box>}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Aktivierungsseite für den Kunden:{' '}
        <Link href={`${window.location.origin}${window.location.pathname}#/aktivierung`} target="_blank" rel="noreferrer">
          {`${window.location.origin}${window.location.pathname}#/aktivierung`}
        </Link>
      </Typography>

      <LizenzDialog
        art={dialog?.art} lizenz={dialog?.lizenz} vorgabe={dialog?.vorgabe} kundeId={kunde.id} module={module}
        onZu={() => setDialog(null)} onFertig={fertig}
      />
      <KundeDialog offen={bearbeiten} kunde={kunde} onZu={() => setBearbeiten(false)} onFertig={() => { setBearbeiten(false); fertig('Kundendaten gespeichert.'); }} />
      <Bestaetigung
        offen={frage?.art === 'lizenz'} titel="Lizenz löschen?" knopf="Lizenz löschen"
        text="Die Lizenz und ihr Aktivierungscode werden entfernt. Das lässt sich nicht rückgängig machen; der Eintrag im Protokoll bleibt."
        onNein={() => setFrage(null)}
        onJa={async () => { await lizenzLoeschen(frage.lizenz.id); setFrage(null); fertig('Lizenz gelöscht.'); }}
      />
      <Bestaetigung
        offen={frage?.art === 'kunde'} titel={`${kunde.anzeige} löschen?`} knopf="Kunde löschen"
        text="Der Kunde wird entfernt. Das lässt sich nicht rückgängig machen; die Einträge im Protokoll bleiben."
        onNein={() => setFrage(null)}
        onJa={async () => { await kundeLoeschen(kunde.id); navigate('/kunden'); }}
      />
    </Box>
  );
}
